<template>
  <div>
    <simple-layout horizontal>
      <basic-card>
        <template #header>
          {{$t('accountBalancesTimelines.accountBalancesTimelines')}}
        </template>
        <form-wrapper :form-title="$t('accountBalancesTimelines.dateInterval')">
          <form-date-input :caption="$t('accountBalancesTimelines.dateFrom')" v-model="dateRange.dateFrom"/>
          <form-date-input :caption="$t('accountBalancesTimelines.dateThru')" v-model="dateRange.dateTo"/>
        </form-wrapper>
        <form-wrapper :form-title="$t('accountBalancesTimelines.aditionalAttributes')">
          <form-checkbox-input :caption="$t('accountBalancesTimelines.color')" v-model="enableScaleBgColor"/>
          <form-checkbox-input :caption="$t('accountBalancesTimelines.showInactive')" v-model="showInactiveAccounts"/>
        </form-wrapper>
        <simple-button @press="getData">{{ $t('accountBalancesTimelines.applyFilter') }}</simple-button>
        <br/>
        <div style="overflow-x: scroll;">
          <data-table v-if="transposed"
                      :configuration="timelineTableConfigTransponed"
                      :data="transformedDataTransponed"
          />
          <data-table v-else
                      :configuration="timelineTableConfig"
                      :data="transformedData"
          />
        </div>
        <simple-button @press="transponView">{{$t('accountBalancesTimelines.transpose')}}</simple-button>
      </basic-card>
    </simple-layout>
  </div>
</template>

<script>
import SimpleLayout from "../components/layout/SimpleLayout";
import BasicCard from "../components/layout/BasicCard";
import FormWrapper from "../components/form/FormWrapper";
import FormDateInput from "../components/form/FormDateInput";
import SimpleButton from "../components/button/SimpleButton";
import Axios from "axios";
import DataTable from "../components/table/DataTable";
import {mapGetters} from "vuex";
import * as d3 from 'd3'
import FormCheckboxInput from "@/components/form/FormCheckboxInput";

export default {
  name: "AccountTimeLinesView",
  components: {FormCheckboxInput, DataTable, SimpleButton, FormDateInput, FormWrapper, BasicCard, SimpleLayout},
  computed: {
    ...mapGetters({account: "banking/account"}),
    timelineTableConfig() {
      return {
        columns: [
          {
            caption: this.$t('accounts.actualDate'),
            datum: d => this.$formats.formatDate(d.date)
          },
          ...this.accountTimeLines.map(timeline => ({
            caption: this.account(timeline.account).name,
            datum: d => this.$formats.formatAmount(d[timeline.account] / 100),
            exportDatum: d => d[timeline.account] / 100,
            style: d => {
              if (!this.enableScaleBgColor) return;
              let cellStyle = {'background-color': `#${Math.round((this.scale(d[timeline.account]) * 125 + 25)).toString(16)}2222`};
              return cellStyle;
            }
          }))
        ]
      }
    },
  },
  data: function () {
    return {
      dateRange: {
        dateFrom: this.$moment().toDate(),
        dateTo: this.$moment().add(12, "d").toDate()
      },
      enableScaleBgColor: false,
      showInactiveAccounts: false,
      minBalance: Number.MIN_VALUE,
      maxBalance: Number.MAX_VALUE,
      scale: null,
      accountTimeLines: [],
      transformedData: [],
      transformedDataTransponed: [],
      transposed: false,
      timelineTableConfigTransponed: {}
    }
  },
  methods: {
    async getData() {
      let timelines = (await Axios.get(`account/api/v1/account-balance-timelines`, {
        params: {
          dateFrom: this.dateRange.dateFrom.split("T")[0],
          dateTo: this.dateRange.dateTo.split("T")[0]
        }
      })).data;
      this.accountTimeLines = timelines.filter(tl => this.account(tl.account).active || this.showInactiveAccounts);
      this.transformData();

    },
    transformData() {
      this.minBalance = Number.MAX_VALUE;
      this.maxBalance = Number.MIN_VALUE;
      if(this.accountTimeLines.length == 0) {
        this.transformedData = []
      }
      this.transformedData = new Array(this.accountTimeLines[0].values.length).fill({})
          .map((value, idx) => {
            let balanceLine = this.accountTimeLines.reduce((timeline, curr) => {
              timeline[curr.account] = curr.values[idx];
              this.minBalance = Math.min(this.minBalance, curr.values[idx])
              this.maxBalance = Math.max(this.maxBalance, curr.values[idx])
              return timeline;
            }, {})
            balanceLine.date = this.$moment(this.dateRange.dateFrom).add(idx, "d")
            return balanceLine
          })
      this.scale = d3.scaleLinear()
          .domain([this.minBalance, this.maxBalance])
          .range([0, 1]);
    },

    transponView() {
      this.transposed = !this.transposed;


      if (this.transposed) {

        const dayDiff = this.$moment(this.dateRange.dateTo).diff(this.dateRange.dateFrom, 'days') + 1;

        this.timelineTableConfigTransponed = {
          columns: [
            {
              caption: this.$t('accounts.account'),
              datum: d => this.account(d.account).name,
            },
            ...new Array(dayDiff).fill({})
                .map((value, idx) => {
                  return {
                    caption: this.$formats.formatDate(this.$moment(this.dateRange.dateFrom).add(idx, "d")),
                    datum: d => this.$formats.formatAmount(d[this.$moment(this.dateRange.dateFrom).add(idx, "d").format('YYYY-MM-DD')] / 100)
                  }
                })
          ]
        }


        this.transformedDataTransponed = this.accountTimeLines.map(timeline => {

          const row = {
            ...timeline
          };

          for (let i = 0; i < dayDiff; i++) {
            const dateString = this.$moment(this.dateRange.dateFrom).add(i, "d").format('YYYY-MM-DD');
            row[dateString] = timeline.values[i];
          }

          return row;
        })
      }

    }
  },
  mounted() {
    this.getData();
  }

}
</script>

<style scoped>

</style>